import { memo } from 'react'
import { Button, Popover } from '@highlight/ui'
import { Coins } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { useChatUsage } from '../../../../hooks/use-chat-usage'
import { useLdFlag } from '../../../../hooks/useLdFlag'
import { useNextMessageModel } from '../../../../hooks/use-next-message-model'
import { forecastContext } from '../../../../utils/context-forecast'
import { ChatUsageDetails, ChatUsageRing, creditsSince, formatCredits, type ChatUsageContext } from './usage-meter'

export const CHAT_USAGE_INDICATOR_FLAG = 'chat-usage-indicator'

/**
 * The composer's context ring, measured against the model the next message
 * will use, with the credits spent and the messages left on hover. Sits with
 * the send controls so the user sees how full the chat is before sending.
 * Hovering previews the details and clicking pins them open.
 * Falls back to a credits-only icon when no context window is known, and is
 * hidden until the chat has usage.
 */
export const UsageIndicator = memo(({ chatId, className }: { chatId: string | undefined; className?: string }) => {
  const enabled = useLdFlag<boolean>(CHAT_USAGE_INDICATOR_FLAG, false)
  const usage = useChatUsage(enabled ? chatId : undefined)
  const nextModel = useNextMessageModel(enabled ? chatId : undefined)

  if (!enabled || !usage || usage.contextTokens === 0) return null

  const contextWindow = nextModel?.contextWindow ?? usage.contextWindow
  const forecast = forecastContext(usage, contextWindow)
  const context: ChatUsageContext | undefined =
    forecast && contextWindow ? { forecast, contextWindow, modelLabel: nextModel?.label } : undefined

  const since = creditsSince(usage)
  const credits = `${formatCredits(usage.credits)} credits used${since ? ` ${since}` : ''}`

  const label = context ? `Context ${Math.round(context.forecast.usedFraction * 100)}% full, ${credits}` : credits

  return (
    <Popover>
      <Popover.Trigger
        openOnHover
        delay={200}
        render={
          <Button
            size="lg"
            variant="ghost"
            shape="circle"
            className={twMerge('text-foreground-secondary [&_svg]:!size-4', className)}
            aria-label={label}
          />
        }
      >
        {context ? <ChatUsageRing usedFraction={context.forecast.usedFraction} /> : <Coins aria-hidden="true" />}
      </Popover.Trigger>
      <Popover.Content side="top" className="w-auto px-2.5 py-1.5 text-[13px] font-medium leading-relaxed">
        <ChatUsageDetails usage={usage} context={context} />
      </Popover.Content>
    </Popover>
  )
})
UsageIndicator.displayName = 'UsageIndicator'
